import { useState } from 'react';
import { Modal, Button, Group, Text, Select, Alert, List, Stack } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useBulkMarkAsPaidMutation } from '@api/documents';
import { usePaymentMethods } from '@api/paymentMethods';
import { CommonHash } from '@types';
import { isDocumentPayable, titleCase } from '@utils';

interface Props {
  opened: boolean;
  close: () => void;
  action: 'paid' | 'unpaid';
  selectedDocuments: CommonHash[];
}

function DocLine({ doc }: { doc: CommonHash }) {
  return (
    <List.Item>
      {doc.dte_type} N° {doc.number}
    </List.Item>
  );
}

function BulkPayDocumentModal({ opened, close, action, selectedDocuments }: Props) {
  const { mutate, isLoading } = useBulkMarkAsPaidMutation();
  const { data: paymentMethodsData } = usePaymentMethods();
  const [paidAt, setPaidAt] = useState<Date | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);

  const paymentMethodOptions = (paymentMethodsData?.data ?? []).map((pm) => ({
    value: String(pm.id),
    label: titleCase(pm.name) ?? pm.name,
  }));

  const nonMarkable = selectedDocuments.filter((doc) => !isDocumentPayable(doc.type as string, doc.status as string));
  const markable = selectedDocuments.filter((doc) => isDocumentPayable(doc.type as string, doc.status as string));

  const alreadyPaid = markable.filter((doc) => doc.paid);
  const willBePaid = markable.filter((doc) => !doc.paid);

  const alreadyUnpaid = markable.filter((doc) => !doc.paid);
  const willBeUnpaid = markable.filter((doc) => doc.paid);

  const isPaid = action === 'paid';
  const hasChanges = isPaid ? markable.length > 0 : willBeUnpaid.length > 0;

  const handleConfirm = () => {
    const uuids = selectedDocuments.map((doc) => doc.uuid as string);
    mutate(
      {
        uuids,
        paid: isPaid,
        paid_at: isPaid && paidAt ? paidAt.toISOString().split('T')[0] : undefined,
        payment_method_id: isPaid && paymentMethodId ? Number(paymentMethodId) : undefined,
      },
      {
        onSuccess: () => {
          setPaidAt(null);
          setPaymentMethodId(null);
          close();
        },
      },
    );
  };

  return (
    <Modal.Root size="md" opened={opened} onClose={close} centered>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title fz={20} fw={600}>
            {isPaid ? 'Marcar como pagados' : 'Marcar como no pagados'}
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Stack gap="md">
            {nonMarkable.length > 0 && (
              <Alert icon={<IconAlertTriangle size={16} />} color="orange" title="Documentos que no serán modificados">
                <Text fz="sm" mb={6}>
                  Los siguientes {nonMarkable.length} documento{nonMarkable.length !== 1 ? 's' : ''} no admiten esta acción (tipo o estado no aplicable):
                </Text>
                <List size="sm" spacing={2}>
                  {nonMarkable.map((doc) => (
                    <DocLine key={doc.uuid as string} doc={doc} />
                  ))}
                </List>
              </Alert>
            )}

            {isPaid && alreadyPaid.length > 0 && (
              <Alert icon={<IconInfoCircle size={16} />} color="blue" title="Documentos ya pagados">
                <Text fz="sm" mb={6}>
                  Los siguientes documentos ya se encuentran en este estado. Se actualizará su método de pago y fecha de pago:
                </Text>
                <List size="sm" spacing={2}>
                  {alreadyPaid.map((doc) => (
                    <DocLine key={doc.uuid as string} doc={doc} />
                  ))}
                </List>
              </Alert>
            )}

            {isPaid ? (
              willBePaid.length > 0 ? (
                <Text fz="sm">
                  Se marcarán como pagados <strong>{willBePaid.length}</strong> documento{willBePaid.length !== 1 ? 's' : ''}.
                </Text>
              ) : markable.length > 0 ? (
                <Text fz="sm" c="dimmed">
                  Todos los documentos seleccionados ya se encuentran pagados. Se actualizará su información de pago.
                </Text>
              ) : null
            ) : (
              <Text fz="sm">
                Se marcarán como no pagados <strong>{willBeUnpaid.length}</strong> documento{willBeUnpaid.length !== 1 ? 's' : ''}.
                {alreadyUnpaid.length > 0 && (
                  <> {alreadyUnpaid.length} ya {alreadyUnpaid.length === 1 ? 'se encuentra' : 'se encuentran'} sin pago y no {alreadyUnpaid.length === 1 ? 'será modificado' : 'serán modificados'}.</>
                )}
              </Text>
            )}

            {isPaid && (
              <>
                <DatePickerInput
                  label="Fecha de pago (opcional)"
                  placeholder="Sin fecha"
                  value={paidAt}
                  onChange={setPaidAt}
                  clearable
                />
                <Select
                  label="Método de pago (opcional)"
                  placeholder="Seleccionar método"
                  data={paymentMethodOptions}
                  value={paymentMethodId}
                  onChange={setPaymentMethodId}
                  clearable
                />
              </>
            )}

            <Group justify="end">
              <Button onClick={close} variant="outline">
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                loading={isLoading}
                color={isPaid ? 'green' : 'red'}
                disabled={!hasChanges}
              >
                Confirmar
              </Button>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

export default BulkPayDocumentModal;
